import React, { ReactChild } from 'react'
import { Rasgo, RASGOS } from '../data/rasgos'
import { shuffle } from '@elgatoylacaja/utils'

export type User = {
  accessToken: string
  experimentDone: boolean
  allowsNews?: boolean
  prevAllowsNews: boolean | null
}

export type AppContextData = {
  datetime: string
  auth: boolean
  visited: Array<string>
  user: User
  experiment: Experiment
  submitted: boolean
}

export type Experiment = {
  rasgos: Rasgo[]
  sliders?: {
    tartamudez_componente_genetico: number
    tartamudez_confianza: number
    zurdera_componente_genetico: number
    zurdera_confianza: number
    esquizofrenia_componente_genetico: number
    esquizofrenia_confianza: number
    autismo_componente_genetico: number
    autismo_confianza: number
    depresión_componente_genetico: number
    depresión_confianza: number
    imc_componente_genetico: number
    imc_confianza: number
    fluencia_verbal_componente_genetico: number
    fluencia_verbal_confianza: number
    visualización_espacial_componente_genetico: number
    visualización_espacial_confianza: number
    velocidad_perceptual_componente_genetico: number
    velocidad_perceptual_confianza: number
    extroversión_componente_genetico: number
    extroversión_confianza: number
    neuroticismo_componente_genetico: number
    neuroticismo_confianza: number
  }
  regressors?: {
    edad: string
    genero: string
    nivel_educativo: string
    orientacion_academica: string
    tiene_hijes: string
    cantidad_hijes: string
    hijes_melli_geme_trilli_si_no: string
    hijes_melli_geme_trilli: string
    tiene_hermanes: string
    cantidad_hermanes: string
    hermanes_melli_geme_trilli: string
    hermanes_melli_geme_trilli_entre_si: string
    slider_economia: string
    slider_politica: string
  }
}

const initialAppContextData: () => AppContextData = () => {
  return {
    datetime: new Date().toLocaleString('es', { hour12: false }),
    auth: false,
    visited: [],
    experiment: {
      rasgos: shuffle(RASGOS),
    },
    user: {
      accessToken: '',
      allowsNews: false,
      experimentDone: false,
      prevAllowsNews: null,
      service: 'email',
    },
    submitted: false,
  }
}

type Omit<T, K extends string | number | symbol> = {
  [P in Exclude<keyof T, K>]: T[P]
}

type Login = {
  type: 'LOGIN'
  user: Omit<User, 'allowsNews'>
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
  allowsNews: boolean
}

type Submited = {
  type: 'SUBMITTED'
}

type SaveSliders = {
  type: 'SAVE_SLIDERS'
  sliders: Experiment['sliders']
}

type SaveRegressors = {
  type: 'SAVE_REGRESSORS'
  data: Experiment['regressors']
}

type ReducerAction =
  | AcceptTerms
  | Login
  | Visit
  | Submited
  | SaveSliders
  | SaveRegressors

const mutateExperimentState = (state: AppContextData) => <
  T extends keyof Experiment
>(
  key: T
) => (data: Experiment[T]) => ({
  ...state,
  experiment: {
    ...state.experiment,
    [key]: data,
  },
})

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'SUBMITTED': {
      return {
        ...state,
        submitted: true,
      }
    }
    case 'LOGIN': {
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          ...action.user,
        },
      }
    }
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
        user: {
          ...state.user!,
          allowsNews: action.allowsNews,
        },
      }
    }
    case 'SAVE_SLIDERS': {
      return mutateExperimentState(state)('sliders')(action.sliders)
    }
    case 'SAVE_REGRESSORS': {
      return mutateExperimentState(state)('regressors')(action.data)
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData(),
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData())

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
